import { createPublicClient, http, parseAbiItem, type Address, type PublicClient } from "viem";
import { config, BRAIN_SUPPLY_CAP } from "../config";
import { marketAbi, stakingVaultAbi, subnetRegistryAbi } from "../abi";
import { l2Chain } from "./chain";

/**
 * Read-side data layer for the explorer surfaces. Uses a JSON-RPC-batching client so sweeping all
 * 64 Brains coalesces into a handful of HTTP requests, and event scans (validator sets, staker
 * positions, fee income) for everything the contracts don't enumerate directly. Every fetcher
 * degrades to empty results when the network isn't configured (preview mode) or the RPC is down.
 */

const batched: PublicClient = createPublicClient({
  chain: l2Chain,
  transport: http(config.l2.rpcUrl, { batch: true }),
});

/**
 * Floor for every event scan. Anchoring to the contracts' deployment block keeps `getLogs` inside
 * the range public RPCs allow on a mature chain (scanning from genesis eventually exceeds their
 * block-range cap and throws — the fetchers would then silently return empty). Falls back to
 * "earliest" when unset (preview / fresh local chain), where the full range is trivially small.
 */
const scanFromBlock = (): bigint | "earliest" =>
  config.l2.deploymentBlock > 0n ? config.l2.deploymentBlock : "earliest";

export interface BrainSummary {
  id: number;
  owner: Address;
  modelURI: string;
  active: boolean;
  registrationFee: bigint;
  ownerSplitBps: number;
  minerShareBps: number;
  miners: number;
  validators: number;
  stake: bigint;
}

export interface NetworkStats {
  activeBrains: number;
  totalMiners: number;
  totalValidators: number;
  totalStaked: bigint;
  pricePerByte: bigint;
  pricePerRequest: bigint;
}

export interface ValidatorRow {
  address: Address;
  stake: bigint;
  /** Lifetime fee income routed to this validator's pool (sum of RewardAccrued). */
  feesEarned: bigint;
}

export interface Position {
  brainId: number;
  validator: Address;
  active: bigint;
  cooldown: bigint;
}

interface SubnetStruct {
  owner: Address;
  modelHash: `0x${string}`;
  modelURI: string;
  registrationFee: bigint;
  ownerSplitBps: number;
  minerShareBps: number;
  createdAt: bigint;
  active: boolean;
}

const validatorRegistered = parseAbiItem(
  "event ValidatorRegistered(uint256 indexed brainId, address indexed validator)",
);
const rewardAccrued = parseAbiItem(
  "event RewardAccrued(uint256 indexed brainId, address indexed validator, uint256 amount)",
);
const stakedEvent = parseAbiItem(
  "event Staked(uint256 indexed brainId, address indexed validator, address indexed staker, uint256 amount)",
);

export async function fetchAllBrains(): Promise<BrainSummary[]> {
  const c = config.l2.contracts;
  try {
    const ids = Array.from({ length: BRAIN_SUPPLY_CAP }, (_, i) => i);
    const [subnets, miners, validators, stakes] = await Promise.all([
      Promise.all(ids.map((id) =>
        batched.readContract({ address: c.subnetRegistry, abi: subnetRegistryAbi, functionName: "getSubnet", args: [BigInt(id)] }),
      )),
      Promise.all(ids.map((id) =>
        batched.readContract({ address: c.subnetRegistry, abi: subnetRegistryAbi, functionName: "minerCount", args: [BigInt(id)] }),
      )),
      Promise.all(ids.map((id) =>
        batched.readContract({ address: c.subnetRegistry, abi: subnetRegistryAbi, functionName: "validatorCount", args: [BigInt(id)] }),
      )),
      Promise.all(ids.map((id) =>
        batched.readContract({ address: c.stakingVault, abi: stakingVaultAbi, functionName: "brainStake", args: [BigInt(id)] }),
      )),
    ]);
    return ids.map((id) => {
      const s = subnets[id] as unknown as SubnetStruct;
      return {
        id,
        owner: s.owner,
        modelURI: s.modelURI,
        active: s.active,
        registrationFee: s.registrationFee,
        ownerSplitBps: Number(s.ownerSplitBps),
        minerShareBps: Number(s.minerShareBps),
        miners: Number(miners[id] as bigint),
        validators: Number(validators[id] as bigint),
        stake: stakes[id] as bigint,
      };
    });
  } catch {
    return [];
  }
}

export async function fetchNetworkStats(brains: BrainSummary[]): Promise<NetworkStats | null> {
  const c = config.l2.contracts;
  try {
    const [totalStaked, ppb, ppr] = await Promise.all([
      batched.readContract({ address: c.stakingVault, abi: stakingVaultAbi, functionName: "totalStaked" }),
      batched.readContract({ address: c.market, abi: marketAbi, functionName: "pricePerByte" }),
      batched.readContract({ address: c.market, abi: marketAbi, functionName: "pricePerRequest" }),
    ]);
    return {
      activeBrains: brains.filter((b) => b.active).length,
      totalMiners: brains.reduce((n, b) => n + b.miners, 0),
      totalValidators: brains.reduce((n, b) => n + b.validators, 0),
      totalStaked: totalStaked as bigint,
      pricePerByte: ppb as bigint,
      pricePerRequest: ppr as bigint,
    };
  } catch {
    return null;
  }
}

/** Validators ever registered on a Brain, filtered to currently-active, with stake + fee income. */
export async function fetchBrainValidators(brainId: number): Promise<ValidatorRow[]> {
  const c = config.l2.contracts;
  try {
    const logs = await batched.getLogs({
      address: c.subnetRegistry,
      event: validatorRegistered,
      args: { brainId: BigInt(brainId) },
      fromBlock: scanFromBlock(),
    });
    const candidates = [...new Set(logs.map((l) => l.args.validator as Address))];
    if (candidates.length === 0) return [];

    const [activeFlags, stakes, rewardLogs] = await Promise.all([
      Promise.all(candidates.map((v) =>
        batched.readContract({ address: c.subnetRegistry, abi: subnetRegistryAbi, functionName: "isValidator", args: [BigInt(brainId), v] }),
      )),
      Promise.all(candidates.map((v) =>
        batched.readContract({ address: c.stakingVault, abi: stakingVaultAbi, functionName: "validatorStake", args: [BigInt(brainId), v] }),
      )),
      batched.getLogs({
        address: c.stakingVault,
        event: rewardAccrued,
        args: { brainId: BigInt(brainId) },
        fromBlock: scanFromBlock(),
      }),
    ]);

    const fees = new Map<string, bigint>();
    for (const l of rewardLogs) {
      const v = (l.args.validator as Address).toLowerCase();
      fees.set(v, (fees.get(v) ?? 0n) + (l.args.amount as bigint));
    }

    return candidates
      .map((address, i) => ({
        address,
        stake: stakes[i] as bigint,
        feesEarned: fees.get(address.toLowerCase()) ?? 0n,
        active: activeFlags[i] as boolean,
      }))
      .filter((r) => r.active)
      .map(({ address, stake, feesEarned }) => ({ address, stake, feesEarned }))
      .sort((a, b) => (b.stake > a.stake ? 1 : b.stake < a.stake ? -1 : 0));
  } catch {
    return [];
  }
}

/** Every (Brain, validator) pool the account has ever staked into, with current balances. */
export async function fetchPositions(account: Address): Promise<Position[]> {
  const c = config.l2.contracts;
  try {
    const logs = await batched.getLogs({
      address: c.stakingVault,
      event: stakedEvent,
      args: { staker: account },
      fromBlock: scanFromBlock(),
    });
    const pools = new Map<string, { brainId: number; validator: Address }>();
    for (const l of logs) {
      const brainId = Number(l.args.brainId as bigint);
      const validator = l.args.validator as Address;
      pools.set(`${brainId}:${validator.toLowerCase()}`, { brainId, validator });
    }
    const entries = [...pools.values()];
    const rows = await Promise.all(
      entries.map(async ({ brainId, validator }) => {
        const [active, cooldown] = await Promise.all([
          batched.readContract({ address: c.stakingVault, abi: stakingVaultAbi, functionName: "stakerBalance", args: [BigInt(brainId), validator, account] }),
          batched.readContract({ address: c.stakingVault, abi: stakingVaultAbi, functionName: "pendingBalance", args: [BigInt(brainId), validator, account] }),
        ]);
        return { brainId, validator, active: active as bigint, cooldown: cooldown as bigint };
      }),
    );
    return rows.filter((r) => r.active > 0n || r.cooldown > 0n);
  } catch {
    return [];
  }
}
